import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import axios from 'axios';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import { DEFAULT_SEGMENT } from '#client/manifest';

import DocumentList from './DocumentList';
import { Wrapper, WrapperContentStyled } from './UploadContents.styles';

import { ACCEPT_DOCS_STATUS, APP_DOCS_STATUS, RESPONSE_STATUS } from '../constants/status';
import { APP_DOCS_TYPES } from '../constants/types';
import { GET_MISSING_DOCS_APPLICATION_QUERY } from '../queries/application';

const initFileCollected = {
  file: null,
  isBack: false
};

function UploadContents({ handleClose }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragEnter, setIsDragEnter] = useState(false);
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { accessToken } = useAuthentication();
  const { refetchAppDocsStatus } = useApplicationStatusContext();
  const [documentSelected, setDocumentSelected] = useState([]);
  const submitButtonId = useEnterSubmit();
  const { createSuccessToast, createErrorToast, createCommonErrorToast } = useToastContext();
  const captureException = useCaptureException();
  const {
    loading: isFetchingMissingDocs,
    data,
    error
  } = useQuery(GET_MISSING_DOCS_APPLICATION_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    },
    skip: !businessEntityUuid,
    noThrow: true,
    skipCache: true
  });

  const memoData = useMemoObject(data);

  const filteredData = useMemo(
    () =>
      (memoData?.application?.documents ?? []).filter((document) =>
        ACCEPT_DOCS_STATUS.includes(document.status)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [!!memoData]
  );

  // handle show toast error
  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const documentFormatted = [];
    filteredData.forEach((item) => {
      const { status, documentType } = item || {};
      const isIdDocument = APP_DOCS_TYPES.ID_DOCUMENT === documentType;
      const isIdDocumentReceivedFront = isIdDocument && APP_DOCS_STATUS.RECEIVED_FRONT === status;
      const initFileCollectedWithBackSide = {
        ...initFileCollected,
        isBack: true
      };

      // add formatted item to array
      documentFormatted.push({
        ...item,
        fileSelected: isIdDocumentReceivedFront
          ? { ...initFileCollectedWithBackSide }
          : { ...initFileCollected },
        // This id only temporary, it be used the to handle show status file is selected or not
        placeholderId: uuidv4()
      });

      /**
       * add one more item (with isBack: true) to array if the documentType
       * is id_document and have status: required
       */
      if (isIdDocument && APP_DOCS_STATUS.REQUIRED === status) {
        documentFormatted.push({
          ...item,
          fileSelected: { ...initFileCollectedWithBackSide },
          placeholderId: uuidv4()
        });
      }
    });
    setDocumentSelected(documentFormatted);
  }, [filteredData]);

  const handleUpload = (index, file) => {
    if (!isSubmitting) {
      const clonedDocumentSelected = [...documentSelected];
      clonedDocumentSelected[index].fileSelected.file = file;
      setDocumentSelected(clonedDocumentSelected);
    }
  };

  const handleRemoveFileSelected = (index) => {
    if (!isSubmitting) {
      const clonedDocumentSelected = [...documentSelected];
      clonedDocumentSelected[index].fileSelected.file = null;
      setDocumentSelected(clonedDocumentSelected);
    }
  };

  const shouldDisableSubmitBtn = useMemo(
    () =>
      documentSelected.length === 0 ||
      documentSelected.some((document) => document?.fileSelected?.file === null),
    [documentSelected]
  );

  const handleSubmit = useCallback(() => {
    const bundleData = [...documentSelected].map(
      ({ id, documentType, fileSelected: { file, isBack } }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          path: generatePath(
            `${DEFAULT_SEGMENT}/applications/:businessEntityUuid/documents/:id/:back`,
            {
              businessEntityUuid,
              id,
              back: isBack ? 'back' : ''
            }
          ),
          formData,
          documentType
        };
      }
    );

    if (bundleData.length) {
      setIsSubmitting(true);
      Promise.allSettled(
        bundleData.map(({ formData, path }) =>
          axios.put(path, formData, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          })
        )
      )
        .then((responses) => {
          setIsSubmitting(false);

          // filter rejected request
          const rejectedRequests = responses.filter(
            (item) => item.status === RESPONSE_STATUS.REJECTED
          );
          if (rejectedRequests?.length > 0) {
            captureException(
              new Error(
                `Your documents did not upload with businessEntityUuid: ${businessEntityUuid}`
              ),
              {
                level: 'warning'
              }
            );
            // show toast error
            createErrorToast({
              title: 'Upload documents',
              description: 'Your documents did not upload. Please try again.'
            });
          } else {
            createSuccessToast({
              title: 'Documents submitted'
            });
            refetchAppDocsStatus();
          }
        })
        .catch((e) => {
          createErrorToast({
            title: 'Something went wrong!',
            error: e
          });
        })
        .finally(() => {
          handleClose(); // close all modal
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, businessEntityUuid, documentSelected]);
  return (
    <>
      <DialogContent>
        <ContainerGrid paddingBottom="20px">
          <FocusGrid>
            <Wrapper>
              <Stack
                alignItems="center"
                height="100%"
                onDragEnter={() => {
                  setIsDragEnter(true);
                }}
                onDrop={() => {
                  setIsDragEnter(false);
                }}
              >
                <WrapperContentStyled>
                  <Stack
                    alignItems="flex-start"
                    mb="28px"
                    spacing="12px"
                  >
                    <Heading
                      size="3xl"
                      weight="bold"
                    >
                      To finish your application, we need the following info.
                    </Heading>
                    <Text
                      color="tertiary"
                      size="lg"
                    >
                      To upload a document, take a photo and make sure it&apos;s the right file size
                      and format. Scanned images or photocopies may not be accepted.
                    </Text>
                  </Stack>
                  {isFetchingMissingDocs ? (
                    <Stack
                      aria-label="loader"
                      paddingTop="16px"
                    >
                      <LinearProgress />
                    </Stack>
                  ) : (
                    <DocumentList
                      documentSelected={documentSelected}
                      handleRemoveFileSelected={handleRemoveFileSelected}
                      handleUpload={handleUpload}
                      isDragEnter={isDragEnter}
                    />
                  )}
                </WrapperContentStyled>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContent>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={shouldDisableSubmitBtn || isSubmitting}
          fullWidth
          id={submitButtonId}
          loading={isSubmitting}
          onClick={handleSubmit}
          size="large"
          variant="contained"
        >
          Submit
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

UploadContents.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default UploadContents;
